/**
 * i18n setup — i18next + react-i18next, sem next-i18next.
 *
 * Detecção de idioma na primeira visita:
 *   1) miller.preferences.v1.locale (localStorage, mantido pelo ThemeProvider)
 *   2) navigator.language (ex: pt-BR → pt)
 *   3) fallback: pt
 *
 * Quando o user troca o idioma na UI, o ThemeProvider:
 *   • chama i18n.changeLanguage(locale)
 *   • atualiza localStorage
 *   • dispara PATCH /me/preferences (background)
 *
 * Os JSONs ficam em frontend/locales/{pt,en,es}.json e são
 * importados estaticamente — bundle único, sem requests HTTP.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import pt from "@/locales/pt.json";
import en from "@/locales/en.json";
import es from "@/locales/es.json";

const PREFERENCES_KEY = "miller.preferences.v1";
const SUPPORTED = ["pt", "en", "es"] as const;

// ─── Custom detector: lê do mesmo localStorage que o ThemeProvider usa ──────
const millerPreferencesDetector = {
  name: "millerPreferences",
  lookup(): string | undefined {
    if (typeof window === "undefined") return undefined;
    try {
      const raw = localStorage.getItem(PREFERENCES_KEY);
      if (!raw) return undefined;
      const prefs = JSON.parse(raw);
      const loc = prefs?.locale;
      return (SUPPORTED as readonly string[]).includes(loc) ? loc : undefined;
    } catch {
      return undefined;
    }
  },
  cacheUserLanguage(lng: string) {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(PREFERENCES_KEY) || "{}";
      const prefs = JSON.parse(raw);
      prefs.locale = lng;
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
    } catch { /* silent */ }
  },
};

const detector = new LanguageDetector();
detector.addDetector(millerPreferencesDetector);

if (!i18n.isInitialized) {
  i18n
    .use(detector)
    .use(initReactI18next)
    .init({
      resources: {
        pt: { translation: pt },
        en: { translation: en },
        es: { translation: es },
      },
      fallbackLng: "pt",
      supportedLngs: SUPPORTED as unknown as string[],
      nonExplicitSupportedLngs: true, // pt-BR → pt, en-US → en, es-AR → es
      load: "languageOnly",
      detection: {
        order: ["millerPreferences", "navigator"],
        caches: ["millerPreferences"],
      },
      interpolation: {
        escapeValue: false, // React já escapa
      },
      react: {
        useSuspense: false, // sem Suspense — evita flicker em SSR
      },
    });
}

export default i18n;
